/* eslint-disable no-underscore-dangle */

/**
 * Usage:   new ScrollSider({
   arrow: ".js-scroll-slider-arrow",
   content: ".js-scroll-slider-content",
   slider: ".js-scroll-slider",
   scrollDistance: 600,
   hideOnDisable: true
 }).init();
 */

class ScrollSider {
  __$arrows;

  __$content;

  __$slider;

  get $arrows() {
    this.__$arrows ||= this.$slider.querySelectorAll(this.selectors.arrow);
    return this.__$arrows;
  }

  get $content() {
    this.__$content ||= this.$slider.querySelector(this.selectors.content);
    return this.__$content;
  }

  get $slider() {
    this.__$slider ||= document.querySelector(this.selectors.slider);
    return this.__$slider;
  }

  constructor(selectors) {
    this.selectors = selectors;
  }

  init() {
    if (this.$arrows.length === 0) return;

    this.#initArrows();
    this.#handleSliderScroll();

    this.$content.addEventListener("scroll", () => this.#handleSliderScroll(), {
      passive: true
    });

    this.#initResizeObserver();
  }

  #initArrows() {
    this.$arrows.forEach($arrow => {
      $arrow.addEventListener("click", () => this.#handleArrowClick($arrow));
    });

    this.#setArrowsVisibility();
  }

  #handleSliderScroll() {
    const { offsetWidth, scrollLeft, scrollWidth } = this.$content;
    const [$arrowLeft, $arrowRight] = this.$arrows;

    const isAtLeftBoundary = scrollLeft === 0;
    const isAtRightBoundary = scrollWidth <= offsetWidth + scrollLeft;

    this.#setArrowState($arrowLeft, isAtLeftBoundary);
    this.#setArrowState($arrowRight, isAtRightBoundary);
  }

  #setArrowState($arrow, shouldDisable) {
    const isDisabled = $arrow.hasAttribute("disabled");

    if (shouldDisable && !isDisabled) {
      $arrow.setAttribute("disabled", true);
      if (this.selectors.hideOnDisable) $arrow.classList.add("hidden");
    } else if (!shouldDisable && isDisabled) {
      $arrow.removeAttribute("disabled");
      if (this.selectors.hideOnDisable) $arrow.classList.remove("hidden");
    }
  }

  #handleArrowClick($arrow) {
    const { direction } = $arrow.dataset;

    // If scrollDistance is provided, use fixed pixel scrolling
    if (this.selectors.scrollDistance) {
      const { scrollLeft } = this.$content;
      const scrollDistance =
        direction === "right" ? this.selectors.scrollDistance : -this.selectors.scrollDistance;
      this.$content.scroll({
        behavior: "smooth",
        left: scrollLeft + scrollDistance
      });
      return;
    }

    // Otherwise, use item-based scrolling
    const segmentWidth = Math.round(this.$content.scrollWidth / this.$content.childElementCount);
    const currentItem = Math.round(this.$content.scrollLeft / segmentWidth);

    let targetLeft = 0;
    if (direction === "left") {
      targetLeft = (currentItem - 2) * segmentWidth;
    } else if (direction === "right") {
      targetLeft = (currentItem + 2) * segmentWidth;
    }

    this.$content.scroll({
      behavior: "smooth",
      left: targetLeft
    });
  }

  #setArrowsVisibility() {
    const { offsetWidth, scrollWidth } = this.$content;

    if (scrollWidth > offsetWidth) {
      this.$arrows.forEach($arrow => {
        $arrow.classList.remove("hidden");
      });
    } else {
      this.$arrows.forEach($arrow => {
        $arrow.classList.add("hidden");
      });
    }
  }

  #initResizeObserver() {
    const resizeObserver = new ResizeObserver(() => {
      this.#setArrowsVisibility();
      this.#handleSliderScroll();
    });

    resizeObserver.observe(this.$content);
  }
}

export default ScrollSider;
